import React from 'react';
import { Link } from 'react-router-dom';
import { PublicNavbar } from '@/components/Layout/PublicNavbar';

/**
 * Términos y Condiciones de Servicio.
 *
 * BORRADOR LEGAL — pendiente de revisión por abogado mexicano antes de su
 * publicación oficial. Contiene marcadores ("Coplaner Inc", domicilio, correos
 * de contacto) que Sergio debe sustituir por la información definitiva de la
 * sociedad mercantil.
 */
const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicNavbar />

      <main className="pt-24 pb-16">
        <article className="max-w-3xl mx-auto px-6 py-12 text-base leading-7 text-neutral-800">
          <header className="mb-10">
            <h1 className="text-h1 font-bold text-brand-primary mb-3">
              Términos y Condiciones de Servicio
            </h1>
            <p className="text-body-sm text-neutral-500">
              Última actualización: 13 de mayo de 2026
            </p>
          </header>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              1. Introducción
            </h2>
            <p>
              CoPlanner es una plataforma en línea (Software as a Service) que
              permite a planificadores de eventos en México organizar bodas,
              quinceañeras y otros eventos sociales, así como administrar a sus
              invitados, proveedores y comunicación por WhatsApp.
            </p>
            <p>
              El servicio es operado por <strong>Coplaner Inc</strong>{' '}
              (en adelante, &laquo;CoPlanner&raquo;, &laquo;nosotros&raquo; o
              &laquo;la Empresa&raquo;). Al crear una cuenta, acceder o utilizar
              la plataforma, usted acepta de forma expresa estos Términos y
              Condiciones, así como nuestro{' '}
              <Link
                to="/aviso-de-privacidad"
                className="text-brand-primary hover:text-brand-primary-hover underline"
              >
                Aviso de Privacidad
              </Link>
              . Si no está de acuerdo con alguno de ellos, deberá abstenerse de
              usar el servicio.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              2. Definiciones
            </h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Planner</strong>: persona física o moral, mayor de
                edad, que contrata CoPlanner para organizar eventos (cliente
                B2B).
              </li>
              <li>
                <strong>Invitado</strong>: persona física cuyos datos (nombre y
                teléfono) son cargados por el Planner en la plataforma para
                gestionar su asistencia al evento. El Invitado es el titular de
                sus datos personales conforme a la LFPDPPP.
              </li>
              <li>
                <strong>Workspace</strong>: espacio de trabajo aislado y
                exclusivo de cada Planner, donde se almacenan sus eventos,
                invitados, proveedores y configuraciones.
              </li>
              <li>
                <strong>Evento</strong>: registro creado dentro de un Workspace
                que representa una boda, XV años u otro evento social.
              </li>
              <li>
                <strong>Suscripción</strong>: plan de pago recurrente (mensual o
                anual) que habilita el acceso a las funciones del servicio.
              </li>
            </ul>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              3. Cuenta y registro
            </h2>
            <p>
              Para usar CoPlanner usted debe (i) ser mayor de edad conforme a la
              legislación mexicana, (ii) proporcionar datos veraces, completos y
              actualizados al momento del registro, y (iii) mantenerlos al día
              durante toda la vigencia de su cuenta.
            </p>
            <p>
              Usted es el único responsable de mantener la confidencialidad de
              su contraseña y de toda actividad realizada bajo su cuenta. Está
              prohibido compartir credenciales de acceso con terceros o permitir
              que varias personas operen bajo una misma cuenta. Notifique de
              inmediato a CoPlanner cualquier uso no autorizado.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              4. Uso aceptable
            </h2>
            <p>Queda estrictamente prohibido utilizar el servicio para:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                Fines ilícitos o contrarios a la moral, las buenas costumbres
                o el orden público.
              </li>
              <li>
                Suplantar la identidad de otra persona, empresa o entidad.
              </li>
              <li>
                Enviar comunicaciones masivas no solicitadas (spam) a los
                Invitados o a cualquier otro destinatario.
              </li>
              <li>
                Realizar scraping, minería de datos automatizada o cualquier
                otra técnica para extraer información de la plataforma sin
                autorización expresa.
              </li>
              <li>
                Realizar ingeniería inversa, descompilar, desensamblar o
                intentar obtener el código fuente del software.
              </li>
              <li>
                Subir o transmitir virus, código malicioso o cualquier elemento
                que pueda dañar la plataforma o a sus usuarios.
              </li>
            </ul>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              5. Mensajería de WhatsApp
            </h2>
            <p>
              CoPlanner facilita el envío de mensajes a Invitados a través de la
              API de WhatsApp Business (operada por Meta y enrutada mediante
              Twilio). El Planner es el único responsable del contenido y la
              legitimidad de los mensajes que envía a sus Invitados, y declara
              contar con el consentimiento previo de los mismos para recibir
              dicha comunicación.
            </p>
            <p>
              Al usar la integración, el Planner se obliga a cumplir las
              Políticas de Comercio y de Mensajería de WhatsApp Business, así
              como las disposiciones aplicables en materia de protección al
              consumidor. Queda prohibido utilizar la plataforma para envío de
              spam, marketing masivo no solicitado o cualquier comunicación que
              no se relacione con el evento al que el Invitado ha sido
              vinculado.
            </p>
            <p>
              CoPlanner podrá suspender el envío de mensajes en caso de detectar
              uso indebido o tras recibir reportes verificables de los
              destinatarios.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              6. Suscripciones y pagos
            </h2>
            <p>
              Los precios vigentes están publicados en{' '}
              <Link
                to="/precios"
                className="text-brand-primary hover:text-brand-primary-hover underline"
              >
                /precios
              </Link>
              . El Planner podrá elegir entre ciclo de facturación mensual o
              anual; el cargo se realizará por adelantado al inicio de cada
              periodo a través del medio de pago registrado.
            </p>
            <p>
              En la versión 1 del servicio no se aplican recargos por mora. Las
              suscripciones no son reembolsables de forma parcial por meses o
              días no utilizados. La cancelación surtirá efectos al final del
              periodo ya pagado, periodo durante el cual el Planner podrá seguir
              utilizando el servicio.
            </p>
            <p>
              CoPlanner podrá actualizar sus precios; cualquier cambio aplicará
              al inicio del siguiente ciclo de facturación y se notificará al
              correo del Planner con al menos 30 días naturales de antelación.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              7. Prueba gratuita
            </h2>
            <p>
              CoPlanner ofrece una prueba gratuita de 15 días naturales, sin
              necesidad de registrar tarjeta de crédito, limitada a 1 (un)
              evento por cuenta. Al cumplirse los 15 días sin contratar una
              suscripción, la cuenta entrará en modo de solo lectura por 5 días
              adicionales y, transcurrido dicho plazo, será archivada.
            </p>
            <p>
              Durante el modo de solo lectura el Planner podrá consultar y
              exportar su información, pero no podrá crear ni modificar
              registros. Una cuenta archivada puede ser reactivada al contratar
              una suscripción dentro de los plazos de retención descritos en el
              Aviso de Privacidad.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              8. Propiedad intelectual
            </h2>
            <p>
              Todos los derechos de propiedad intelectual sobre el software, el
              diseño, las marcas, los logotipos y la documentación de CoPlanner
              son propiedad exclusiva de Coplaner Inc o de sus licenciantes, y
              están protegidos por las leyes mexicanas e internacionales en la
              materia.
            </p>
            <p>
              El Planner conserva la totalidad de los derechos sobre los datos y
              contenidos que sube a la plataforma (lista de invitados,
              proveedores, mensajes, tareas, etc.). CoPlanner cuenta únicamente
              con una licencia limitada, no exclusiva y revocable para procesar
              dichos datos con el único fin de prestar el servicio.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              9. Limitación de responsabilidad
            </h2>
            <p>
              El servicio se presta &laquo;tal cual&raquo; y &laquo;según
              disponibilidad&raquo;. En la medida máxima permitida por la ley
              aplicable, CoPlanner no será responsable por daños indirectos,
              incidentales, especiales, consecuenciales o punitivos, incluyendo
              pero no limitándose a pérdida de utilidades, pérdida de datos,
              pérdida de oportunidad o daño moral, derivados o relacionados con
              el uso o la imposibilidad de uso del servicio.
            </p>
            <p>
              La responsabilidad total agregada de CoPlanner frente al Planner
              por cualquier reclamo se limitará al monto efectivamente pagado
              por el Planner en los 12 (doce) meses anteriores al hecho que
              originó la reclamación.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              10. Suspensión y terminación
            </h2>
            <p>
              CoPlanner podrá suspender o dar de baja la cuenta del Planner, sin
              responsabilidad alguna, en caso de violación a estos Términos, uso
              indebido del servicio, falta de pago o requerimiento de autoridad
              competente.
            </p>
            <p>
              El Planner podrá cancelar su suscripción en cualquier momento
              desde la sección de cuenta dentro de la aplicación. La cancelación
              surtirá efectos al final del periodo de facturación en curso.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              11. Datos del usuario
            </h2>
            <p>
              El tratamiento de los datos personales del Planner y de los
              Invitados se rige por el{' '}
              <Link
                to="/aviso-de-privacidad"
                className="text-brand-primary hover:text-brand-primary-hover underline"
              >
                Aviso de Privacidad
              </Link>
              , que forma parte integral de estos Términos. Le recomendamos
              leerlo con detenimiento antes de utilizar el servicio.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              12. Modificaciones
            </h2>
            <p>
              CoPlanner se reserva el derecho de modificar estos Términos en
              cualquier momento. Los cambios serán publicados en esta misma
              página, con su fecha de &laquo;Última actualización&raquo;.
              Cuando los cambios sean materiales, se notificarán al Planner por
              correo electrónico con al menos 30 días naturales de antelación a
              su entrada en vigor. El uso continuado del servicio después de la
              fecha de vigencia constituye la aceptación de los nuevos Términos.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              13. Ley aplicable y jurisdicción
            </h2>
            <p>
              Estos Términos se rigen e interpretan conforme a las leyes
              vigentes en los Estados Unidos Mexicanos. Para la interpretación,
              ejecución y cumplimiento de los mismos, las partes se someten
              expresamente a la jurisdicción de los tribunales competentes en la
              Ciudad de México, renunciando a cualquier otro fuero que pudiera
              corresponderles en razón de sus domicilios presentes o futuros.
            </p>
          </section>

          <section className="space-y-4 mb-10">
            <h2 className="text-h2 font-semibold text-brand-primary mt-2 mb-2">
              14. Contacto
            </h2>
            <p>
              Para cualquier duda, aclaración o notificación legal relacionada
              con estos Términos, puede escribirnos a{' '}
              <a
                href="mailto:legal@coplaner.mx"
                className="text-brand-primary hover:text-brand-primary-hover underline"
              >
                legal@coplaner.mx
              </a>
              .
            </p>
          </section>

          <div className="border-t border-neutral-200 pt-6">
            <Link
              to="/"
              className="text-brand-primary hover:text-brand-primary-hover underline focus-visible-ring"
            >
              &larr; Volver al inicio
            </Link>
          </div>
        </article>
      </main>
    </div>
  );
};

export default Terms;
